/**
 * A Creo card's pop-up: the skill's or agent's picture, name and
 * description, how it runs, and the project the chat will live in. An agent
 * says it runs on its own and spends from the AI budget before Use it. Use it
 * adds the card to the message, beside any already picked. Without a picture
 * it is 560 wide. On a phone it is a sheet from the bottom with Use it full
 * width. It opens with the dialogs' own motion, the same every time (user
 * call, 12 Oct: growing out of the card measured a different origin each
 * open).
 */
import { useRef, useState } from "react";
import { X } from "lucide-react";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import type { WorkshopQuickCreateItem } from "@/types/workshop.type";
import { howItRuns, kindLabel, pickId } from "../quickCreate";
import { launchCreoFlight } from "../creoFlight";
import { QuickCreatePicture } from "./QuickCreateParts";
import WorkshopProjectPicker, { usePickableProjects } from "./WorkshopProjectPicker";

type Props = {
  item: WorkshopQuickCreateItem | null;
  onClose: () => void;
  /** The project the pop-up starts on (the new chat's pick); else the latest. */
  projectId: string | null;
  canWrite: boolean;
  isPhone: boolean;
  /** Already in the message: Use it reads Added. */
  added?: boolean;
  /** No room left among the picks (too many, or too many agents). */
  full?: boolean;
  /** Use it: a new chat in that project with the pill in its composer (null: no project yet). */
  onUse: (item: WorkshopQuickCreateItem, projectId: string | null) => void;
};

export default function WorkshopQuickCreateDetail({
  item,
  onClose,
  projectId,
  canWrite,
  isPhone,
  added,
  full,
  onUse,
}: Props) {
  // The last card shown stays inside while the pop-up closes, so it shrinks
  // back into the card whole instead of emptying (and narrowing) first.
  const [shown, setShown] = useState(item);
  if (item && item !== shown) setShown(item);
  return (
    <KitModal
      isOpen={item !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      aria-label={shown ? shown.name : "Creos"}
      hideCloseButton
      modalClassName={shown?.imageUrl ? "ws-dialog ws-qc-detail ws-qc-detail--pictured" : "ws-dialog ws-qc-detail"}
      overlayClassName="ws-dialog-overlay"
      data-phone={isPhone || undefined}
    >
      {shown ? (
        <DetailBody
          key={shown.key}
          item={shown}
          projectId={projectId}
          canWrite={canWrite}
          isPhone={isPhone}
          added={added}
          full={full}
          onClose={onClose}
          onUse={onUse}
        />
      ) : null}
    </KitModal>
  );
}

function DetailBody({
  item,
  projectId,
  canWrite,
  isPhone,
  added = false,
  full = false,
  onClose,
  onUse,
}: Omit<Props, "item"> & { item: WorkshopQuickCreateItem }) {
  const { active, latest } = usePickableProjects();
  const [picked, setPicked] = useState<string | null>(projectId);
  const project = active.find((p) => p.id === picked) ?? latest;
  const pictured = Boolean(item.imageUrl);

  const nameRef = useRef<HTMLHeadingElement>(null);
  const use = () => {
    launchCreoFlight(pickId(item), nameRef.current);
    onUse(item, active.length === 0 ? null : (project?.id ?? null));
  };

  return (
    <div className="ws-qc-detail-inner">
      {isPhone ? <span className="ws-sheet-grabber" aria-hidden="true" /> : null}
      <div className="ws-qc-detail-head">
        <h2 className="ws-qc-detail-kicker">Creos · {kindLabel(item.kind)}</h2>
        <button type="button" className="ws-qc-detail-close" onClick={onClose} aria-label="Close">
          <X aria-hidden="true" />
        </button>
      </div>
      <div className="ws-qc-detail-middle">
        {pictured ? <QuickCreatePicture item={item} className="ws-qc-detail-pic" /> : null}
        <div className="ws-qc-detail-words">
          <h3 ref={nameRef} className="ws-qc-detail-name">
            {item.name}
          </h3>
          {item.description ? <p className="ws-qc-detail-desc">{item.description}</p> : null}
          <dl className="ws-qc-facts">
            <div className="ws-qc-fact">
              <dt>How</dt>
              <dd>{howItRuns(item)}</dd>
            </div>
          </dl>
        </div>
      </div>
      <div className="ws-qc-detail-foot">
        <WorkshopProjectPicker
          project={project}
          projects={active}
          onPick={setPicked}
          canCreate={canWrite}
          className="ws-qc-detail-project"
        />
        <span className="ws-qc-detail-push" />
        <KitButton variant="ghost" onClick={onClose}>
          Cancel
        </KitButton>
        <KitButton onClick={use} isDisabled={!canWrite || added || full}>
          {added ? "Added" : "Use it"}
        </KitButton>
      </div>
    </div>
  );
}
